import React, { useCallback, useEffect, useState } from "react";
import {
  Row, Col, Card, Table, Button, Input, notification, Modal,
  Select, Tag, Space, Tooltip, Typography, Badge, Tabs
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  SearchOutlined, EyeOutlined, MessageOutlined,
  CheckCircleOutlined, ClockCircleOutlined, SyncOutlined,
  ReloadOutlined, UserOutlined, PhoneOutlined,
  SendOutlined
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const STATUS_CONFIG = {
  Pending:   { color: 'orange', icon: <ClockCircleOutlined />, label: 'Pending' },
  pending:   { color: 'orange', icon: <ClockCircleOutlined />, label: 'Pending' },
  InProcess: { color: 'blue',   icon: <SyncOutlined spin />,   label: 'In Process' },
  Completed: { color: 'green',  icon: <CheckCircleOutlined />, label: 'Completed' },
};

const TYPE_COLORS = {
  'General': 'default', 'Payment Issue': 'red', 'Account Problem': 'volcano',
  'Scholarship': 'purple', 'Grievance': 'orange', 'Suggestion': 'cyan',
};

export default function InquiryList() {
  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab,    setActiveTab]    = useState('all');
  const [viewItem,     setViewItem]     = useState(null);
  const [replyItem,    setReplyItem]    = useState(null);
  const [replyText,    setReplyText]    = useState('');
  const [replying,     setReplying]     = useState(false);

  const PAGE_SIZE = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit: PAGE_SIZE, sortField: 'id', sortOrder: 'desc', search,
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      const res = await axios.get(`${baseUrl}/inquiry/list?${params}`, { headers: authHeader() });
      if (res.data.status) {
        setData(res.data.result || []);
        setTotal(res.data.totalCount || 0);
      }
    } catch {
      notification.error({ message: 'Failed to load inquiries', placement: 'topRight' });
    } finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReply = async () => {
    if (!replyText.trim()) {
      notification.warning({ message: 'Reply cannot be empty', placement: 'topRight' });
      return;
    }
    setReplying(true);
    try {
      const res = await axios.patch(
        `${baseUrl}/inquiry/reply/${replyItem.id}`,
        { resolved_remark: replyText },
        { headers: authHeader() }
      );
      if (res.data.status) {
        notification.success({ message: 'Reply sent to member', placement: 'topRight' });
        setReplyItem(null);
        setReplyText('');
        fetchData();
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Failed', placement: 'topRight' });
    } finally { setReplying(false); }
  };

  const pendingCount   = data.filter(d => d.status === 'Pending' || d.status === 'pending').length;
  const completedCount = data.filter(d => d.status === 'Completed').length;

  const filteredData = activeTab === 'pending'
    ? data.filter(d => d.status === 'Pending' || d.status === 'pending')
    : activeTab === 'completed' ? data.filter(d => d.status === 'Completed')
    : data;

  const columns = [
    {
      title: '#', dataIndex: 'id', key: 'id', width: 60,
      render: v => <Text type="secondary" style={{ fontSize: 12 }}>#{v}</Text>,
    },
    {
      title: 'Member', key: 'member', width: 180,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{r.name || '—'}</div>
          <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
            <PhoneOutlined style={{ fontSize: 10 }} />{r.phone_no || '—'}
          </div>
        </div>
      ),
    },
    {
      title: 'Type', dataIndex: 'received_call', key: 'received_call', width: 130,
      render: v => v ? <Tag color={TYPE_COLORS[v] || 'default'} style={{ fontSize: 10 }}>{v}</Tag> : '—',
    },
    {
      title: 'Inquiry / Message', dataIndex: 'inquiry_regards', key: 'inquiry_regards',
      render: v => (
        <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0, fontSize: 13, maxWidth: 320 }}>
          {v || '—'}
        </Paragraph>
      ),
    },
    {
      title: 'Admin Reply', dataIndex: 'resolved_remark', key: 'resolved_remark', width: 200,
      render: v => v
        ? <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0, fontSize: 12, color: '#15803d', maxWidth: 200 }}>{v}</Paragraph>
        : <Text type="secondary" style={{ fontSize: 11 }}>No reply yet</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: v => {
        const cfg = STATUS_CONFIG[v] || STATUS_CONFIG.Pending;
        return <Tag color={cfg.color} icon={cfg.icon} style={{ fontSize: 11 }}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Date', dataIndex: 'createdAt', key: 'createdAt', width: 110,
      render: v => v
        ? <Text style={{ fontSize: 11 }}>{new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Text>
        : '—',
    },
    {
      title: 'Actions', key: 'actions', width: 100, fixed: 'right',
      render: (_, r) => (
        <Space>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => setViewItem(r)} style={{ borderRadius: 6 }} />
          </Tooltip>
          <Tooltip title={r.resolved_remark ? 'Edit Reply' : 'Reply to Member'}>
            <Button
              size="small" icon={<MessageOutlined />}
              type={r.resolved_remark ? 'default' : 'primary'}
              style={{ borderRadius: 6, ...(!r.resolved_remark ? { background: '#7a0000', borderColor: '#7a0000' } : {}) }}
              onClick={() => { setReplyItem(r); setReplyText(r.resolved_remark || ''); }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Inquiries & Grievances</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Member portal submissions — view and reply</Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Refresh</Button>
        </Col>
      </Row>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        {[
          { label: 'Total',     value: total,         color: '#7a0000', bg: '#e8f0fe' },
          { label: 'Pending',   value: pendingCount,   color: '#92400e', bg: '#fef3c7' },
          { label: 'Completed', value: completedCount, color: '#15803d', bg: '#f0fdf4' },
        ].map(s => (
          <Col xs={8} key={s.label}>
            <Card size="small" style={{ textAlign: 'center', borderRadius: 10, background: s.bg, border: 'none' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{s.label}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
        {/* Filters */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name, phone, subject…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            allowClear
            style={{ width: 260, borderRadius: 8 }}
          />
          <Select
            placeholder="Filter by Status"
            value={statusFilter || undefined}
            onChange={v => { setStatusFilter(v || ''); setPage(1); }}
            allowClear
            style={{ width: 150 }}
          >
            <Option value="Pending">Pending</Option>
            <Option value="InProcess">In Process</Option>
            <Option value="Completed">Completed</Option>
          </Select>
          {(search || statusFilter) && (
            <Button size="small" onClick={() => { setSearch(''); setStatusFilter(''); setPage(1); }}>
              Clear
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div style={{ padding: '0 20px' }}>
          <Tabs activeKey={activeTab} onChange={setActiveTab} size="small">
            <TabPane tab="All" key="all" />
            <TabPane
              key="pending"
              tab={
                <Badge count={pendingCount} offset={[6, 0]}>
                  <span style={{ paddingRight: 8 }}>Pending</span>
                </Badge>
              }
            />
            <TabPane tab="Completed" key="completed" />
          </Tabs>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            current: page, pageSize: PAGE_SIZE, total,
            onChange: p => setPage(p),
            showTotal: t => `Total ${t} inquiries`,
            size: 'small',
          }}
          style={{ padding: '0 0 8px' }}
          rowClassName={r =>
            (r.status === 'Pending' || r.status === 'pending') && !r.resolved_remark
              ? 'inquiry-row-pending' : ''
          }
        />
      </Card>

      {/* ── View Modal ── */}
      <Modal
        open={!!viewItem}
        onCancel={() => setViewItem(null)}
        title={<span><EyeOutlined style={{ color: '#7a0000', marginRight: 8 }} />Inquiry Details #{viewItem?.id}</span>}
        width={600}
        footer={[
          <Button key="reply" type="primary" icon={<MessageOutlined />}
            style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
            onClick={() => { setReplyItem(viewItem); setReplyText(viewItem?.resolved_remark || ''); setViewItem(null); }}>
            Reply to Member
          </Button>,
          <Button key="close" onClick={() => setViewItem(null)} style={{ borderRadius: 8 }}>Close</Button>,
        ]}
      >
        {viewItem && (
          <div>
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <Row gutter={[16, 10]}>
                <Col span={12}>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>MEMBER NAME</div>
                  <div style={{ fontWeight: 700 }}><UserOutlined style={{ marginRight: 5 }} />{viewItem.name || '—'}</div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>PHONE</div>
                  <div style={{ fontWeight: 700 }}><PhoneOutlined style={{ marginRight: 5 }} />{viewItem.phone_no || '—'}</div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>TYPE</div>
                  <Tag color={TYPE_COLORS[viewItem.received_call] || 'default'}>{viewItem.received_call || 'General'}</Tag>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>STATUS</div>
                  <Tag color={STATUS_CONFIG[viewItem.status]?.color || 'orange'}
                    icon={STATUS_CONFIG[viewItem.status]?.icon}>
                    {STATUS_CONFIG[viewItem.status]?.label || viewItem.status}
                  </Tag>
                </Col>
                <Col span={24}>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>DATE</div>
                  <div style={{ fontSize: 12 }}>{viewItem.createdAt ? new Date(viewItem.createdAt).toLocaleString('en-IN') : '—'}</div>
                </Col>
              </Row>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 8 }}>
                <MessageOutlined style={{ marginRight: 5 }} />Inquiry / Message
              </div>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {viewItem.inquiry_regards || '—'}
              </div>
            </div>

            {viewItem.resolved_remark && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', marginBottom: 8 }}>
                  <CheckCircleOutlined style={{ marginRight: 5 }} />Admin Reply (sent to member)
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 14px', fontSize: 13, lineHeight: 1.7, color: '#166534', whiteSpace: 'pre-wrap' }}>
                  {viewItem.resolved_remark}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Reply Modal ── */}
      <Modal
        open={!!replyItem}
        onCancel={() => { setReplyItem(null); setReplyText(''); }}
        title={<span><SendOutlined style={{ color: '#7a0000', marginRight: 8 }} />Reply to Member — #{replyItem?.id}</span>}
        width={580}
        footer={[
          <Button key="cancel" onClick={() => { setReplyItem(null); setReplyText(''); }} style={{ borderRadius: 8 }}>Cancel</Button>,
          <Button key="send" type="primary" loading={replying} icon={<SendOutlined />}
            style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
            onClick={handleReply}>
            Send Reply
          </Button>,
        ]}
      >
        {replyItem && (
          <div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 }}>
              <Space wrap>
                <span><UserOutlined style={{ marginRight: 4 }} /><b>{replyItem.name}</b></span>
                <span>·</span>
                <span><PhoneOutlined style={{ marginRight: 4 }} />{replyItem.phone_no}</span>
                <Tag color={TYPE_COLORS[replyItem.received_call] || 'default'} style={{ margin: 0 }}>
                  {replyItem.received_call || 'General'}
                </Tag>
              </Space>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Their Message</div>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 130, overflowY: 'auto' }}>
                {replyItem.inquiry_regards}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 6 }}>Your Reply *</div>
              <TextArea
                rows={5}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Type your reply here… This will be visible to the member in their portal."
                maxLength={1000}
                showCount
                style={{ borderRadius: 8 }}
              />
            </div>

            {replyItem.resolved_remark && (
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, fontSize: 12, color: '#713f12' }}>
                ⚠️ This inquiry already has a reply. Submitting will overwrite it.
              </div>
            )}
          </div>
        )}
      </Modal>

      <style>{`
        .inquiry-row-pending { background: #fffbeb !important; }
        .inquiry-row-pending:hover > td { background: #fef3c7 !important; }
      `}</style>
    </div>
  );
}
